/**
 * Afrikaans: AI tools
 *
 * What this file covers: The AI tools page: writing with skills, answers from the business's documents, image to text, saved skills and the history of AI calls with what each cost.
 *
 * Tone: Friendly and plain, for business owners trying AI for the first time. Say what each tool does and what it costs in a few words; never promise more than the AI can do.
 *
 * Afrikaans: Standard South African Afrikaans. Address the reader as "jy". Use the same Afrikaans word for the
 * same English term everywhere; menu.ts and common.ts set the words the other files follow. Afrikaans must stay
 * complete: the release checks fail if a key is missing.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const ai: Catalog = {
  'ai.title': 'KI-gereedskap',
  'ai.description': 'Skryf, som op, vertaal, antwoord uit jou eie dokumente en lees prente, met die KI-model wat jy kies. Elke gebruik word op jou krediete gehef.',
  'ai.banner': 'Dieselfde gereedskap werk uit jou eie stelsels met ’n API-sleutel wat die KI-toestemming het.',
  'ai.banner.keys': 'API-sleutels',
  'ai.tabs.write': 'Skryf',
  'ai.tabs.ask': 'Vra jou dokumente',
  'ai.tabs.vision': 'Prent na teks',
  'ai.tabs.skills': 'My vaardighede',
  'ai.tabs.history': 'Geskiedenis',
  'ai.chargeNote': 'Die meeste wat dit kan kos, word eers van jou krediete opsy gesit, en jy betaal net wat die KI werklik gebruik het.',
  'ai.model.label': 'Model',
  'ai.model.none': 'Geen KI-model is op die oomblik aangeskakel nie.',
  'ai.model.unpriced': 'Nog geen prys nie.',
  'ai.model.priceText': '{{read}} per 10 000 tokens wat dit lees, {{written}} per 10 000 wat dit skryf (’n token is omtrent vier letters).',
  'ai.model.priceUnit': '{{price}} per {{unit}}.',
  'ai.model.outsideSa': 'Loop buite Suid-Afrika',
  'ai.result.copy': 'Kopieer',
  'ai.result.copied': 'Gekopieer.',
  'ai.result.stub': 'KI is nog nie op hierdie platform opgestel nie, so dit is ’n plaasvervanger-antwoord en niks werkliks is gevra nie.',
  'ai.result.cost': '{{model}} · {{cost}} · {{read}} tokens gelees, {{written}} geskryf',
  'ai.error.credits.title': 'Nie genoeg krediet nie',
  'ai.error.credits.body': 'Dit kan tot {{needed}} kos en jy het {{available}} vry om te bestee. Laai op, of vra vir iets korters.',
  'ai.error.credits.topUp': 'Laai op',
  'ai.error.AI_MODEL_UNAVAILABLE': 'Geen KI-model is op die oomblik aangeskakel nie. Probeer asseblief later weer.',
  'ai.error.AI_MODEL_NOT_PRICED': 'Daardie model het nog geen prys nie, so dit kan nie gebruik word nie. Kies ’n ander een.',
  'ai.error.AI_PROVIDER_ERROR': 'Die KI kon nou nie antwoord nie. Niks is gehef nie. Probeer asseblief weer.',
  'ai.error.BILLING_UNAVAILABLE': 'Jou krediet kon nou nie nagegaan word nie, so niks is gedoen nie. Probeer asseblief oor ’n oomblik weer.',
  'ai.write.title': 'Skryf met ’n vaardigheid',
  'ai.write.skill': 'Vaardigheid',
  'ai.write.own': '{{name}} (joune)',
  'ai.write.run': 'Skryf dit',
  'ai.skill.summarise.name': 'Som op',
  'ai.skill.summarise.description': 'Kort ’n teks in tot wat saak maak.',
  'ai.skill.write.name': 'Skryf',
  'ai.skill.write.description': 'Skryf ’n artikel, plasing, e-pos, nuusbrief, boodskap of storie oor ’n onderwerp.',
  'ai.skill.rewrite.name': 'Herskryf',
  'ai.skill.rewrite.description': 'Sê dieselfde ding in ’n ander toon, of duideliker.',
  'ai.skill.translate.name': 'Vertaal',
  'ai.skill.translate.description': 'Vertaal ’n teks in ’n ander taal.',
  'ai.skill.reply.name': 'Stel ’n antwoord op',
  'ai.skill.reply.description': 'Stel ’n antwoord op ’n kliënt se boodskap op.',
  'ai.skill.extract.name': 'Haal besonderhede uit',
  'ai.skill.extract.description': 'Vind benoemde besonderhede in ’n teks, soos ’n naam, ’n e-posadres of ’n bestelnommer.',
  'ai.skill.classify.name': 'Sorteer in kategorieë',
  'ai.skill.classify.description': 'Sê aan watter van jou kategorieë ’n teks behoort.',
  'ai.input.default': 'Laat die KI besluit',
  'ai.input.text.label': 'Teks',
  'ai.input.text.hint': 'Die teks om aan te werk.',
  'ai.input.topic.label': 'Onderwerp',
  'ai.input.topic.hint': 'Waaroor om te skryf, bv. ons skoolfees op Saterdag.',
  'ai.input.kind.label': 'Wat om te skryf',
  'ai.input.kind.hint': 'Die soort skryfwerk.',
  'ai.input.tone.label': 'Toon',
  'ai.input.tone.hint': 'Hoe dit moet klink, bv. vriendelik, formeel, prettig of oortuigend.',
  'ai.input.length.label': 'Lengte',
  'ai.input.length.hint': 'Hoe lank dit moet wees.',
  'ai.input.audience.label': 'Gehoor',
  'ai.input.audience.hint': 'Vir wie dit is, bv. ouers, kliënte of personeel.',
  'ai.input.details.label': 'Besonderhede',
  'ai.input.details.hint': 'Feite om in te sluit: datums, name, pryse en plekke. Die KI maak dit nooit op nie.',
  'ai.input.language.label': 'Taal',
  'ai.input.language.hint': 'Die taal om in te skryf, bv. Engels, Afrikaans of isiZulu.',
  'ai.input.format.label': 'Uitleg',
  'ai.input.format.hint': 'Paragrawe of kolpunte.',
  'ai.input.message.label': 'Die kliënt se boodskap',
  'ai.input.message.hint': 'Wat die kliënt geskryf het.',
  'ai.input.fields.label': 'Besonderhede om te vind',
  'ai.input.fields.hint': 'Geskei deur kommas, bv. naam, e-pos, bestelnommer, datum.',
  'ai.input.labels.label': 'Kategorieë',
  'ai.input.labels.hint': 'Geskei deur kommas, bv. rekeninge, klagte, verkope, ander.',
  'ai.choice.short': 'Kort',
  'ai.choice.medium': 'Medium',
  'ai.choice.long': 'Lank',
  'ai.choice.paragraph': 'Paragrawe',
  'ai.choice.bullets': 'Kolpunte',
  'ai.choice.article': 'Artikel',
  'ai.choice.social_post': 'Sosiale media-plasing',
  'ai.choice.email': 'E-pos',
  'ai.choice.newsletter': 'Nuusbrief',
  'ai.choice.whatsapp_message': 'WhatsApp-boodskap',
  'ai.choice.story': 'Storie',
  'ai.choice.product_description': 'Produkbeskrywing',
  'ai.ask.title': 'Vra jou dokumente',
  'ai.ask.body': 'Die KI antwoord net uit jou kennisbasis en wys waar die antwoord vandaan kom. Voeg dokumente by op die blad',
  'ai.ask.question': 'Vraag',
  'ai.ask.questionHint': 'Vra dit soos ’n kliënt of ’n kollega dit sou vra.',
  'ai.ask.run': 'Vra',
  'ai.ask.unsure': 'Jou dokumente sê dit nie. Voeg die antwoord by jou kennisbasis, of vra dit op ’n ander manier.',
  'ai.ask.sources': 'Waar dit gekyk het',
  'ai.ask.source': '{{name}}, bladsy {{page}}',
  'ai.vision.title': 'Prent na teks',
  'ai.vision.picture': 'Prent',
  'ai.vision.pictureHint': '’n PNG-, JPEG-, GIF- of WebP-prent kleiner as 3,75 MB.',
  'ai.vision.wrongType': 'Kies ’n PNG-, JPEG-, GIF- of WebP-prent.',
  'ai.vision.tooBig': 'Daardie prent is te groot. Kies een kleiner as 3,75 MB.',
  'ai.vision.task': 'Wat om te doen',
  'ai.vision.task.describe': 'Beskryf die prent',
  'ai.vision.task.text': 'Lees elke woord daarop',
  'ai.vision.task.question': 'Beantwoord ’n vraag daaroor',
  'ai.vision.question': 'Vraag',
  'ai.vision.questionHint': 'bv. Wat is die totaal op hierdie faktuur?',
  'ai.vision.run': 'Lees dit',
  'ai.skills.title': 'My vaardighede',
  'ai.skills.body': 'Stoor instruksies, agtergrond en ’n toon een keer, en voer dit dan uit Skryf of uit jou eie stelsels uit.',
  'ai.skills.new': 'Nuwe vaardigheid',
  'ai.skills.empty': 'Jy het nog geen eie vaardighede nie',
  'ai.skills.emptyHint': 'Byvoorbeeld: ’n skoolnuusbrief in ’n prettige toon, met jou skool se naam en waardes as agtergrond.',
  'ai.skills.saved': '{{name}} is gestoor.',
  'ai.skills.deleted': '{{name}} is uitgevee.',
  'ai.skills.deleteTitle': 'Vee {{name}} uit?',
  'ai.skills.deleteBody': 'Stelsels wat hierdie vaardigheid volgens sy kode uitvoer, sal gesê word dat dit nie meer bestaan nie.',
  'ai.skills.name': 'Naam',
  'ai.skills.nameHint': 'Wat jy dit noem, bv. Skoolnuusbrief.',
  'ai.skills.code': 'Kode',
  'ai.skills.codeHint': 'Hoe jou stelsels dit noem: kleinletters, syfers en koppeltekens.',
  'ai.skills.description': 'Beskrywing',
  'ai.skills.descriptionHint': 'Een reël wat sê waarvoor dit is.',
  'ai.skills.instructions': 'Instruksies',
  'ai.skills.instructionsHint': 'Wat die KI moet doen, soos jy dit vir ’n mens sou sê, bv. Skryf ’n nuusbriefartikel vir ouers oor die gegewe onderwerp.',
  'ai.skills.context': 'Agtergrond',
  'ai.skills.contextHint': 'Wat die KI altyd moet weet: wie jy is, jou gehoor, feite en frases om te gebruik.',
  'ai.skills.tone': 'Toon',
  'ai.skills.toneHint': 'bv. prettig, warm, formeel.',
  'ai.skills.creativity': 'Kreatiwiteit',
  'ai.skills.creativityHint': 'Presies bly naby aan wat dit gegee word; kreatief skryf vryer. Claude-modelle kies dit self, so dit geld vir die Nova-modelle.',
  'ai.skills.creativity.precise': 'Presies',
  'ai.skills.creativity.balanced': 'Gebalanseerd',
  'ai.skills.creativity.creative': 'Kreatief',
  'ai.skills.maxTokens': 'Langste antwoord (tokens)',
  'ai.skills.maxTokensHint': 'Omtrent vier letters per token. ’n Langer limiet sit meer krediet opsy terwyl dit loop.',
  'ai.skills.kb': 'Gebruik my kennisbasis',
  'ai.skills.kbHint': 'Gee die KI ook die gedeeltes uit jou dokumente wat pas by wat dit gevra word.',
  'ai.history.title': 'Geskiedenis',
  'ai.history.body': 'Elke KI-oproep van hierdie blad en van jou API-sleutels, met wat dit gekos het.',
  'ai.history.empty': 'Nog geen KI-oproepe nie.',
  'ai.history.when': 'Wanneer',
  'ai.history.tool': 'Gereedskap',
  'ai.history.skill': 'Vaardigheid: {{skill}}',
  'ai.history.by': 'Deur',
  'ai.history.byKey': 'API-sleutel {{name}}',
  'ai.history.byConsole': 'Hierdie blad',
  'ai.history.cost': 'Koste',
  'ai.history.preview': 'Antwoord',
  'ai.tool.text': 'Teks',
  'ai.tool.vision': 'Prent na teks',
  'ai.tool.answer': 'Vra jou dokumente',
  'ai.tool.skill': 'Vaardigheid',
  'ai.tool.speech': 'Teks na spraak',
  'ai.tool.transcribe': 'Spraak na teks',
  'ai.tool.image': 'Prent',
  'ai.status.done': 'Klaar',
  'ai.status.held': 'Besig',
  'ai.status.failed': 'Misluk',
  'ai.status.refused': 'Nie genoeg krediet nie',
  'ai.tabs.speech': 'Teks na spraak',
  'ai.tabs.transcribe': 'Spraak na teks',
  'ai.tabs.pictures': 'Prente',
  'ai.unit.cost': 'Koste {{cost}}',
  'ai.unit.saved': '\'n afskrif is in jou mediabiblioteek',
  'ai.unit.save': 'Hou \'n afskrif in my mediabiblioteek',
  'ai.unit.saveHint': 'Sodat jy dit op WhatsApp kan stuur of weer kan gebruik. Dit word gestoor soos enige lêer wat jy oplaai.',
  'ai.unit.open': 'Maak oop',
  'ai.unit.download': 'Laai af',
  'ai.speech.title': 'Teks na spraak',
  'ai.speech.text': 'Teks om voor te lees',
  'ai.speech.textHint': '{{count}} van {{max}} karakters. Gehef per 1 000 karakters.',
  'ai.speech.voice': 'Stem',
  'ai.speech.voiceOption': '{{name}} ({{language}})',
  'ai.speech.voicesFailed': 'Die stemme kon nie nou gelaai word nie. Probeer asseblief weer.',
  'ai.speech.format': 'Lêertipe',
  'ai.speech.format.mp3': 'MP3, speel oral',
  'ai.speech.format.ogg': 'OGG, vir WhatsApp-stemnotas',
  'ai.speech.run': 'Lees dit voor',
  'ai.speech.characters': '{{count}} karakters voorgelees.',
  'ai.transcribe.title': 'Spraak na teks',
  'ai.transcribe.recording': 'Opname',
  'ai.transcribe.recordingHint': '\'n MP3-, WAV-, OGG-, FLAC- of M4A-opname van tot 20 MB, soos \'n WhatsApp-stemnota.',
  'ai.transcribe.tooBig': 'Daardie opname is te groot. Kies een van tot 20 MB.',
  'ai.transcribe.language': 'Taal',
  'ai.transcribe.language.auto': 'Werk dit uit die opname uit',
  'ai.transcribe.language.en-ZA': 'Engels (Suid-Afrika)',
  'ai.transcribe.language.af-ZA': 'Afrikaans',
  'ai.transcribe.language.zu-ZA': 'isiZulu',
  'ai.transcribe.language.en-GB': 'Engels (Verenigde Koninkryk)',
  'ai.transcribe.language.en-US': 'Engels (Verenigde State)',
  'ai.transcribe.run': 'Skryf dit uit',
  'ai.transcribe.chargeNote': 'Gehef per minuut opname, per sekonde, met \'n minimum van 15 sekondes. Die krediet word eers opsy gesit, en niks word gehef as dit misluk nie.',
  'ai.transcribe.running': 'Word nog uitgeskryf. \'n Lang opname kan \'n paar minute neem. Die teks verskyn hier, en onder Geskiedenis as jy hierdie bladsy verlaat.',
  'ai.transcribe.failed': 'Die opname kon nie uitgeskryf word nie. Niks is gehef nie.',
  'ai.transcribe.silent': 'Geen woorde is in hierdie opname gehoor nie.',
  'ai.transcribe.length': 'Die opname is {{seconds}} sekondes lank.',
  'ai.pictures.title': 'Maak prente',
  'ai.pictures.prompt': 'Beskrywing',
  'ai.pictures.promptHint': 'Wat die prent moet wys, bv. \'n plakkaat van \'n skool se pretdag met ballonne en \'n springkasteel, in helder kleure.',
  'ai.pictures.avoid': 'Laat uit',
  'ai.pictures.avoidHint': 'Wat die prent nie moet wys nie, bv. skrif of mense.',
  'ai.pictures.size': 'Vorm',
  'ai.pictures.size.square': 'Vierkantig',
  'ai.pictures.size.landscape': 'Breed',
  'ai.pictures.size.portrait': 'Hoog',
  'ai.pictures.count': 'Hoeveel',
  'ai.pictures.outsideSa': 'Hierdie model werk buite Suid-Afrika, so jou beskrywing word soontoe gestuur om die prente te maak.',
  'ai.pictures.run': 'Maak prente',
  'ai.pictures.alt': 'Prent {{number}}: {{prompt}}',
  'ai.history.detail': '{{when}} · {{model}} · {{cost}} · {{status}}',
  'ai.history.saved': 'Lêers in jou mediabiblioteek gehou: {{count}}.'
};

export default ai;
